"use client";

import { useState } from "react";
import Link from "next/link";
import { ArrowUpRight, Coins, Sparkles } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useAuth } from "@/components/auth/AuthProvider";
import { PLAN_QUOTAS, deriveCurrentPlan, type PlanTier } from "@/lib/authz/plans";
import { TokenPackDialog } from "@/components/billing/TokenPackDialog";
import { usePreviewAwareAdmin } from "@/lib/home/planPreview";
import { trackClientEvent } from "@/lib/tracking/client";

export function TokenChip() {
  const { displayTokens, billing, isAdmin } = useAuth();
  const { previewTier } = usePreviewAwareAdmin();
  const planTier: PlanTier =
    previewTier ?? (isAdmin ? "studio" : deriveCurrentPlan(billing?.personalSubscriptionData ?? null));
  const monthlyGrant = PLAN_QUOTAS[planTier].aiTokensPerMonth;
  const [popoverOpen, setPopoverOpen] = useState(false);
  const [tokenDialogOpen, setTokenDialogOpen] = useState(false);

  return (
    <>
      <Popover open={popoverOpen} onOpenChange={setPopoverOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            aria-label={`${displayTokens.toLocaleString()} AI tokens — open token options`}
            className="hidden items-center gap-1.5 rounded-full border border-primary/30 bg-primary/10 px-2.5 py-1.5 font-mono text-xs text-primary transition-colors hover:border-primary/50 hover:bg-primary/15 sm:inline-flex"
          >
            <Coins className="h-3.5 w-3.5" />
            <span>{displayTokens.toLocaleString()}</span>
          </button>
        </PopoverTrigger>
        <PopoverContent align="end" className="w-80">
          <div className="flex items-start gap-3">
            <span className="grid h-9 w-9 flex-none place-items-center rounded-xl border border-primary/30 bg-primary/10 text-primary">
              <Coins className="h-4 w-4" />
            </span>
            <div className="min-w-0 flex-1">
              <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-muted-foreground">
                AI tokens
              </p>
              <p className="mt-0.5 text-2xl font-semibold tracking-tight text-foreground">
                {displayTokens.toLocaleString()}
              </p>
              <p className="mt-0.5 text-xs text-muted-foreground">
                {monthlyGrant > 0
                  ? `${monthlyGrant.toLocaleString()} granted monthly on your plan`
                  : "Your plan does not include a monthly token grant"}
              </p>
            </div>
          </div>

          <button
            type="button"
            onClick={() => {
              setPopoverOpen(false);
              setTokenDialogOpen(true);
              trackClientEvent("checkout_started", { kind: "tokens", source: "token_chip" });
            }}
            className="mt-4 flex w-full items-center justify-between gap-2 rounded-xl border border-primary/30 bg-primary px-3.5 py-2.5 text-sm font-semibold text-primary-foreground shadow-[0_18px_50px_hsl(var(--primary)/0.22)] transition hover:-translate-y-0.5 hover:bg-primary/90"
          >
            <span className="inline-flex items-center gap-2">
              <Sparkles className="h-4 w-4" />
              Get more tokens
            </span>
            <ArrowUpRight className="h-4 w-4" />
          </button>

          <Link
            href="/account/tokens"
            className="mt-2 flex items-center justify-between gap-2 rounded-xl border border-border/60 bg-card/60 px-3.5 py-2 text-xs text-muted-foreground transition hover:border-accent/40 hover:text-foreground"
          >
            See balance &amp; usage
            <ArrowUpRight className="h-3.5 w-3.5" />
          </Link>

          <p className="mt-3 text-[11px] leading-5 text-muted-foreground/80">
            Subscription tokens expire 30 days after they&rsquo;re granted. Purchased token packs do not expire.
          </p>
        </PopoverContent>
      </Popover>

      <TokenPackDialog open={tokenDialogOpen} onOpenChange={setTokenDialogOpen} />
    </>
  );
}
